import React from 'react'
import { INTERVAL_DAY, INTERVAL_HOUR, INTERVAL_MINUTE } from 'containers/CustomDashboard/Dashboard/details/chartConfigurations'
import { InfoBubbleIcon, BarsVerticalIcon, SearchActionIcon, ItemListIcon } from 'images/common'
import { createChart, updateChart, deleteChart } from '../../api/dashboard'

export const modeList = [
  { label: 'Ask about data', icon: <InfoBubbleIcon />, value: 'askData', badge: 'AI' },
  { label: 'Compare periods', icon: <BarsVerticalIcon />, value: 'comparePeriods', badge: 'AI' },
  { label: 'Find anomalies', icon: <SearchActionIcon />, value: 'findAnomalies', badge: 'AI' },
  { label: 'Summarize insights', icon: <ItemListIcon />, value: 'summarizeInsights', badge: 'AI' }
]

export const TIME_WINDOW_OPTIONS = [
  // Note: 'All' maps to backend enum 'none'
  { label: 'All', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 0, totalDays: 0, apiValue: 'none' },
  { label: 'Last 5 minutes', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 5, totalDays: 0.005, apiValue: 'last_5m' },
  { label: 'Last 15 minutes', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 15, totalDays: 0.011, apiValue: 'last_15m' },
  { label: 'Last 30 minutes', start: '', end: '', timeInterval: INTERVAL_MINUTE, timeQuantity: 30, totalDays: 0.021, apiValue: 'last_30m' },
  { label: 'Last 1 hour', start: '', end: '', timeInterval: INTERVAL_HOUR, timeQuantity: 1, totalDays: 0.042, apiValue: 'last_1h' },
  { label: 'Last 3 hours', start: '', end: '', timeInterval: INTERVAL_HOUR, timeQuantity: 3, totalDays: 0.125, apiValue: 'last_3h' },
  { label: 'Last 6 hours', start: '', end: '', timeInterval: INTERVAL_HOUR, timeQuantity: 6, totalDays: 0.25, apiValue: 'last_6h' },
  { label: 'Last 12 hours', start: '', end: '', timeInterval: INTERVAL_HOUR, timeQuantity: 12, totalDays: 0.5, apiValue: 'last_12h' },
  { label: 'Last 1 day', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 1, totalDays: 1, apiValue: 'last_1d' },
  { label: 'Last 7 days', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 7, totalDays: 7, apiValue: 'last_7d' },
  { label: 'Last 15 days', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 15, totalDays: 15, apiValue: 'last_15d' },
  { label: 'Last 30 days', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 30, totalDays: 30, apiValue: 'last_30d' },
  { label: 'Last 90 days', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 90, totalDays: 90, apiValue: 'last_90d' },
  { label: 'Last 180 days', start: '', end: '', timeInterval: INTERVAL_DAY, timeQuantity: 180, totalDays: 180, apiValue: 'last_180d' }
]

export const RESPONSE_STYLE_OPTIONS = [
  { label: 'Headlines only', value: 'headlines only' },
  { label: 'Add details', value: 'add details' }
]

export const TONE_OPTIONS = [
  { label: 'Executive', value: 'executive' },
  { label: 'Operational', value: 'operational' },
  { label: 'Simple', value: 'simple' }
]

export const FORMATTING_OPTIONS = [
  { label: 'Default', value: 'default' },
  { label: 'Custom', value: 'custom' }
]

export const UNITS_OPTIONS = [
  { label: 'Metric', value: 'metric' },
  { label: 'Imperial', value: 'imperial' }
]

export const CURRENCY_OPTIONS = [
  { label: 'USD', value: 'USD' },
  { label: 'EUR', value: 'EUR' },
  { label: 'GBP', value: 'GBP' }
]

export const DECIMALS_OPTIONS = [
  { label: 'None', value: 'none' },
  { label: '0.0', value: '.0' },
  { label: '0.00', value: '.00' }
]

export const CHART_COUNT_OPTIONS = [
  { label: '1 chart', value: 1 },
  { label: '2 charts', value: 2 },
  { label: '3 charts', value: 3 },
  { label: '4 charts', value: 4 },
  { label: '5 charts', value: 5 }
]

export const AUDIENCE_OPTIONS = [
  { label: 'Executive', value: 'executive' },
  { label: 'Operations', value: 'operations' }
]

export const MODEL_OPTIONS = [
  { label: 'In-Depth', value: 'gpt54', badge: 'GPT-5.4' },
  { label: 'Balanced', value: 'gpt54Mini', badge: 'GPT-5.4 Mini' },
  { label: 'Fast', value: 'gpt54Nano', badge: 'GPT-5.4 Nano' }
]

export const INSERT_CHART_TYPES = {
  visoChart: {
    id: 'createChart',
    mutation: createChart,
    buttonLabel: 'Insert',
    buttonDone: 'Inserted' 
  },
  reviewVisoChart: {
    id: 'updateChart',
    mutation: updateChart,
    buttonLabel: 'Apply',
    buttonDone: 'Applied' 
  },
  removeVisoChart: {
    id: 'deleteChart',
    mutation: deleteChart,
    buttonLabel: 'Remove',
    buttonDone: 'Removed' 
  },
  recommendVisoChart: {
    id: 'createChart',
    mutation: createChart,
    buttonLabel: 'Apply',
    buttonDone: 'Applied' 
  },
  improveVisoChart: {
    id: 'updateChart',
    mutation: updateChart,
    buttonLabel: 'Apply',
    buttonDone: 'Applied' 
  }
}

export const DAYS_TO_INCLUDE_OPTIONS = [
  { label: '5', value: 'five', isDefault: true },
  { label: '7', value: 'seven' },
  { label: '14', value: 'fourteen' }
]

export const GRANULARITY_OPTIONS = [
  { label: '15 min', value: 'fifteenMin', isDefault: true },
  { label: '30 min', value: 'thirtyMin' },
  { label: '1 hour', value: 'oneHour' },
  { label: '1 day', value: 'oneDay' }
]

export const SERIES_COUNT_OPTIONS = [
  { label: '1', value: 'one', isDefault: true },
  { label: '2', value: 'two' },
  { label: '3', value: 'three' },
]